/**
 * Client-side report export — System Health + CI runs.
 *
 * Pure, dependency-free serializers used by the "Export report"
 * dropdowns on the System Health board and the Test Suite view. The
 * APIs are NOT involved: the report is generated from the data that's
 * already on-screen, so what the staff member exports is exactly what
 * they're looking at.
 *
 * Two output flavors per surface:
 *   - Markdown (.md) — human-readable, copy-paste-friendly into a
 *     ticket or email. Headings, lists, fenced details.
 *   - JSON (.json) — the raw response shape, lightly trimmed to the
 *     fields the UI is showing. Useful as an attachment / diff base.
 *
 * Filenames carry a UTC timestamp (compact, no spaces) so a folder of
 * exports sorts chronologically. ET is shown in the document body for
 * human reading; UTC in the filename for machine sorting.
 */

import type { AdminCiRun, AdminCiRunsResponse, AdminHealthReport } from '@sill/types';

import { formatIsoCompact } from './format.js';

/**
 * Trigger a browser download of `content` as a file. Uses a transient
 * Blob URL + a hidden <a> click. Safe to call multiple times in a row
 * (each call gets its own object URL, revoked after the click).
 */
export function triggerDownload(filename: string, mime: string, content: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Some browsers require the element to be in the DOM to honor the
  // click — append, click, remove in the same tick.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the download a tick to start before releasing the URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Compact UTC stamp for filenames — `YYYYMMDD-HHMMSSZ`. Stable, sortable,
 * no spaces or colons. Falls back to `nowZ` if the Date is invalid.
 */
function utcStampForFilename(d: Date = new Date()): string {
  if (Number.isNaN(d.getTime())) return 'nowZ';
  const iso = d.toISOString(); // 2026-06-23T14:05:33.221Z
  return iso.replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z').replace('T', '-');
}

// ----- Markdown serializers --------------------------------------------

/** Escape a markdown table cell value (pipes only — newlines never
 *  reach here because the server-side detail is single-line). */
function escapeCell(value: string): string {
  return value.replace(/\|/g, '\\|');
}

function renderHealthMarkdown(report: AdminHealthReport): string {
  const lines: string[] = [];
  lines.push('# Sill — System Health report');
  lines.push('');
  lines.push(`- Generated: ${formatIsoCompact(report.generated_at)}`);
  lines.push(`- Run duration: ${report.duration_ms} ms`);
  lines.push(
    `- Summary: ${report.summary.pass} passing · ${report.summary.warn} warnings · ${report.summary.fail} failing`,
  );
  lines.push('');
  lines.push('## Checks');
  lines.push('');
  if (report.checks.length === 0) {
    lines.push('_No checks returned._');
    return lines.join('\n');
  }
  // Fails first, then warns, then passes — matches the on-screen order.
  const rank: Record<string, number> = { fail: 0, warn: 1, pass: 2 };
  const sorted = [...report.checks].sort((a, b) => {
    const sa = rank[a.status] ?? 3;
    const sb = rank[b.status] ?? 3;
    if (sa !== sb) return sa - sb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  for (const c of sorted) {
    lines.push(`### ${c.status.toUpperCase()} — ${c.label}`);
    lines.push('');
    lines.push(`- id: \`${c.id}\``);
    lines.push(`- category: ${c.category}`);
    lines.push(`- duration: ${c.duration_ms} ms`);
    if (c.detail.length > 0) {
      lines.push('- detail:');
      lines.push('');
      lines.push('  ```');
      lines.push(`  ${c.detail}`);
      lines.push('  ```');
    }
    lines.push('');
  }
  return lines.join('\n').trimEnd() + '\n';
}

function renderCiRunsMarkdown(payload: AdminCiRunsResponse): string {
  const lines: string[] = [];
  lines.push('## CI runs (GitHub Actions)');
  lines.push('');
  lines.push(`- Configured: ${payload.configured ? 'yes' : 'no'}`);
  lines.push(`- Generated: ${formatIsoCompact(payload.generated_at)}`);
  if (payload.error !== undefined && payload.error.length > 0) {
    lines.push(`- Error: \`${payload.error}\``);
  }
  lines.push('');
  if (payload.runs.length === 0) {
    lines.push('_No runs to show._');
    return lines.join('\n').trimEnd() + '\n';
  }
  // Same fails-first ordering as the on-screen list.
  const ranked = rankCiRuns(payload.runs);
  lines.push('| Conclusion | Status | Workflow | Title | Branch | SHA | Updated | Link |');
  lines.push('| --- | --- | --- | --- | --- | --- | --- | --- |');
  for (const r of ranked) {
    const conclusion = r.conclusion ?? '—';
    lines.push(
      [
        conclusion,
        r.status,
        escapeCell(r.workflow_name),
        escapeCell(r.display_title),
        escapeCell(r.branch),
        r.head_sha,
        formatIsoCompact(r.updated_at),
        `[run #${r.id}](${r.html_url})`,
      ]
        .map((s) => ` ${s} `)
        .join('|')
        .replace(/^/, '|')
        .replace(/$/, '|'),
    );
  }
  return lines.join('\n').trimEnd() + '\n';
}

/**
 * Build the combined Health + (optional) CI report as Markdown. If
 * `ci` is undefined, the CI section is omitted — staff can export
 * Health alone from the Health view without loading CI data.
 */
export function buildReportMarkdown({
  health,
  ci,
}: {
  health?: AdminHealthReport;
  ci?: AdminCiRunsResponse;
}): string {
  const parts: string[] = [];
  if (health !== undefined) {
    parts.push(renderHealthMarkdown(health));
  } else {
    parts.push('# Sill — Test Suite report\n');
    parts.push(`Generated: ${formatIsoCompact(new Date().toISOString())}\n`);
  }
  if (ci !== undefined) {
    parts.push(renderCiRunsMarkdown(ci));
  }
  return parts.join('\n');
}

// ----- JSON serializers ------------------------------------------------

/**
 * Bundle the raw report payloads as JSON. Mirrors the wire shapes (no
 * lossy projection) so the export can be diffed against another run.
 * Top-level wrapper adds an `exported_at` ISO so the file is
 * self-describing even after a rename.
 */
export function buildReportJson({
  health,
  ci,
}: {
  health?: AdminHealthReport;
  ci?: AdminCiRunsResponse;
}): string {
  const payload = {
    exported_at: new Date().toISOString(),
    health: health ?? null,
    ci: ci ?? null,
  };
  return JSON.stringify(payload, null, 2) + '\n';
}

// ----- Public download wrappers ----------------------------------------

export function downloadHealthMarkdown(report: AdminHealthReport, ci?: AdminCiRunsResponse): void {
  const md = buildReportMarkdown(ci !== undefined ? { health: report, ci } : { health: report });
  triggerDownload(`sill-health-${utcStampForFilename()}.md`, 'text/markdown', md);
}

export function downloadHealthJson(report: AdminHealthReport, ci?: AdminCiRunsResponse): void {
  const json = buildReportJson(ci !== undefined ? { health: report, ci } : { health: report });
  triggerDownload(`sill-health-${utcStampForFilename()}.json`, 'application/json', json);
}

export function downloadCiMarkdown(ci: AdminCiRunsResponse): void {
  const md = buildReportMarkdown({ ci });
  triggerDownload(`sill-ci-runs-${utcStampForFilename()}.md`, 'text/markdown', md);
}

export function downloadCiJson(ci: AdminCiRunsResponse): void {
  const json = buildReportJson({ ci });
  triggerDownload(`sill-ci-runs-${utcStampForFilename()}.json`, 'application/json', json);
}

// ----- Helpers shared with the views -----------------------------------

/**
 * Sort CI runs the way the on-screen list does: failures first, then
 * cancelled, then everything else. Stable secondary sort by
 * `updated_at` desc so newest sits at the top inside each group.
 */
export function rankCiRuns(runs: ReadonlyArray<AdminCiRun>): AdminCiRun[] {
  const rank = (r: AdminCiRun): number => {
    if (r.conclusion === 'failure') return 0;
    if (r.conclusion === 'cancelled') return 1;
    if (r.status === 'in_progress' || r.status === 'queued') return 2;
    if (r.conclusion === 'success') return 3;
    return 4;
  };
  return [...runs].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    // Newest first within the same rank.
    if (a.updated_at > b.updated_at) return -1;
    if (a.updated_at < b.updated_at) return 1;
    return 0;
  });
}
